import { Component, HostBinding, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { ConfirmService, PointEditMode, PointEditModeService, RecentValuesService, StorageService, WorkflowService } from '@luzula/luzula-lib';
import { DataService } from 'src/app/data.service';
import { Command, NavbarService } from 'src/app/navbar.service';
import { ValidationService } from '../../validation.service';
import { dateString, punktartSvgUrl, istPunktartOhneSymbol, punktartOhneSymbolLabel, punktartOhneSymbolColor } from 'src/app/data-model';
import { ProjectService } from 'src/app/project.service';
import { merkeGewaehltePunktart } from '../punktart-favoriten';

@Component({
    standalone: false,
    selector: 'bp-hl-area-container',
    templateUrl: 'hl-area-container.component.html',
    styleUrls: ['hl-area.component.css'],
    
})
export class HlAreaContainerComponent implements OnInit, OnDestroy{
  @Input() name!: string;
  @Input() scrollX = false;
  bp: any;
  /** Punktart beim Oeffnen — nur eine Aenderung daran zaehlt als Wahl. */
  private punktartBeimLaden = '';
  dateString = dateString;
  punktartSvgUrl = punktartSvgUrl;
  istPunktartOhneSymbol = istPunktartOhneSymbol;
  punktartOhneSymbolLabel = punktartOhneSymbolLabel;
  punktartOhneSymbolColor = punktartOhneSymbolColor;
  private dirtySubscription?: Subscription;
  private routeSubscription?: Subscription;
  private authChangedSubscription?: Subscription;

  private ns = inject(NavbarService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private vs = inject(ValidationService);
  private ds = inject(DataService);
  aus = inject(AuthService);
  private confirmService = inject(ConfirmService);
  ps = inject(ProjectService);
  private editMode = inject(PointEditModeService);
  private ws = inject(WorkflowService);
  private storage = inject(StorageService);
  private recentValues = inject(RecentValuesService);

  /** Capability-Check: User kann Bearbeiter-Aktionen — inkl. Owner/Entwickler-Bypass. */
  get canBeBearbeiter(): boolean {
    return this.ps.canActAsBearbeiter();
  }

  /**
   * Korrektur-Modus (G7) anbietbar? Nur wenn der aktuelle User für DIESEN Punkt
   * die reine Kontrolleur-Rolle einnimmt UND der Punkt in einem korrigierbaren
   * Status (`kontrolle`/`abgelehnt`) ist. `ws.canEditPoint` kapselt genau das
   * (Owner/Entwickler sind 'admin' und editieren ohnehin im Bearbeiter-Modus).
   * Jede Korrektur wird beim Save automatisch protokolliert.
   */
  get canCorrect(): boolean {
    // Mehrfachrollen-sicher (2026-06-18): reiner Kontrolleur-Korrektur-Anteil,
    // damit der „Korrigieren"-Button bei einem Doppelrollen-User (B+K) NICHT
    // fälschlich auch auf eigenen `bearbeitung`-Punkten erscheint. Kein
    // getWorkflowRole/currentRole-Kollaps mehr.
    return !!this.bp && this.ws.canKontrolleurCorrect(this.bp);
  }

  /**
   * Review-/Kommentar-Stift-Modus für DIESEN Punkt anbietbar? Status-aware:
   * nur eingetragener Kontrolleur UND Status `in_pruefung` (Punkt angenommen),
   * nicht eingefroren / nicht fremdes Projekt. `ws.canKontrolleurReview` kapselt
   * das (User-Entscheidung 2026-06-24, PERMISSIONS.md §5.2).
   *
   * Ersetzt das frühere status-agnostische `canActAsKontrolleur()`-Gate des
   * „Kontrolleur-Modus"-Knopfes — dieses zeigte den Stift fälschlich auf jedem
   * Punkt (auch `bearbeitung`/`abgeschlossen`).
   */
  get canReview(): boolean {
    return !!this.bp && this.ws.canKontrolleurReview(this.bp);
  }

  /**
   * Modus-Switch (btn-group) sichtbar, sobald für DIESEN Punkt mindestens ein
   * Kontrolleur-Modus (Review/Stift oder Korrigieren) verfügbar ist. Ein reiner
   * Bearbeiter ohne Kontrolleur-Modus braucht keinen Umschalter.
   */
  get showEditModeSwitch(): boolean {
    return this.canReview || this.canCorrect;
  }

  get editModeValue(): PointEditMode {
    return this.editMode.current()?.mode ?? 'edit';
  }

  /**
   * Wird per CSS-Selector ausgewertet, um in der HL-Area:
   * - den Cursor auf .highlight-area zu Text/Pen umzuschalten und
   * - readonly-Felder (.to-be-checked) optisch deaktiviert zu zeigen.
   * Auswertung als Getter, weil `aus.commenting` ein plain property ist —
   * Angular Change-Detection ruft den Getter pro Zyklus auf.
   */
  @HostBinding('class.review-mode')
  get isReviewMode(): boolean {
    return this.aus.commenting;
  }

  switchEditMode(mode: PointEditMode): void {
    if (!this.bp) return;
    this.editMode.setMode(this.bp._id, mode);
    // commenting/pen am Auth-Service mitziehen — Severity-Buttons schalten den
    // Stift, der Modus selbst steuert nur, ob ueberhaupt kommentiert wird.
    this.aus.commenting = (mode === 'review');
    if (mode === 'review' && this.aus.pen !== 'rubber') {
      this.aus.pen = 'danger';
    }
  }

	ngOnInit(): void {
		this.routeSubscription = this.route.parent?.data.subscribe(data => {
			this.bp = data['bp'];
			this.punktartBeimLaden = this.bp?.type ?? '';
		});

		this.ns.subscribe(Command.goBackToPoint, async () => {
			if (!this.vs.dirty.value) {
				this.navigateBack();
				return;
			}
			const result = await this.confirmService.confirm({
				title: 'Ungespeicherte Änderungen',
				message: 'Es gibt ungespeicherte Änderungen an diesem Punkt.',
				confirmText: 'Verwerfen',
				confirmClass: 'btn-warning',
				extraText: 'Speichern & zurück',
				extraClass: 'btn-success',
				cancelText: 'Bleiben'
			});
			if (result === 'extra') {
				// Speichern und dann zurück — nur wenn schreibberechtigt (Test-Fund #1).
				if (this.ps.canIWritePoint(this.bp)) {
					await this.ds.saveDocument(this.bp, { user: this.aus.currentUser });
					this.merkePunktartNachSpeichern();
				}
				this.vs.dirty.next(false);
				this.navigateBack();
			} else if (result === true) {
				// Verwerfen: Punkt aus DB neu laden
				this.ds.getDocument$(this.bp._id).subscribe((freshBp: any) => {
					Object.assign(this.bp, this.ds.updateBohrpunkt(freshBp));
					this.vs.dirty.next(false);
					this.navigateBack();
				});
			}
		});

		this.dirtySubscription = this.vs.dirty.subscribe(() => this.initSaveButton());
		this.authChangedSubscription = this.aus.authChanged.subscribe(() => this.initSaveButton());
		this.initSaveButton();
	}

	private navigateBack() {
		const currentRoute = this.router.url;
		const newRoute = currentRoute.substring(0, currentRoute.lastIndexOf('/'));
		this.router.navigate([newRoute]);
	}

	private initSaveButton() {
		if (this.ps.canIWritePoint(this.bp) && this.vs.dirty.value) {
			this.ns.subscribe(Command.savePoint, () => this.onSave());
		} else {
			this.ns.unsubscribe(Command.savePoint);
		}
	}

	private onSave() {
		if (!this.vs.dirty.value) return;
		this.ds.saveDocument(this.bp, { user: this.aus.currentUser });
		this.merkePunktartNachSpeichern();
		this.vs.dirty.next(false);
	}

	/**
	 * Eine hier gewaehlte Punktart zaehlt erst jetzt — beim Speichern. Wer nur
	 * ein anderes Feld aendert, aendert damit auch die Vorbelegung nicht.
	 */
	private merkePunktartNachSpeichern(): void {
		merkeGewaehltePunktart(this.recentValues, this.storage, this.bp?.type ?? '', this.punktartBeimLaden);
		this.punktartBeimLaden = this.bp?.type ?? '';
	}

	ngOnDestroy(): void {
		this.ns.unsubscribe(Command.goBackToPoint);
		this.ns.unsubscribe(Command.savePoint);
		this.dirtySubscription?.unsubscribe();
		this.routeSubscription?.unsubscribe();
		this.authChangedSubscription?.unsubscribe();
	}
}
